import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CakeDesign, PlacedTopping } from './cakeTypes';
import { CAKE_TOPPINGS, getSheetById, normalizeRotationDeg } from './cakeTypes';

/** 토핑: `toppingImageUrl`이 있으면 PNG, 없으면 SVG. `CAKE_TOPPINGS`와 맞출 것. */

function clientPointToSvgViewBox(svg: SVGSVGElement, clientX: number, clientY: number) {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  return pt.matrixTransform(ctm.inverse());
}

function pointerAngleDeg(cx: number, cy: number, px: number, py: number): number {
  return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
}

export interface DecoratedCakePreviewProps {
  design: CakeDesign;
  /** 픽셀 기준 전체 너비 */
  size?: number;
  /** 클릭 시 편지 보기 */
  interactive?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  /** 장식 단계: 케이크 위 자유 좌표 드롭·탭 배치 */
  slotEditEnabled?: boolean;
  /** 탭으로 올릴 장식 선택 시 해당 토핑 id */
  armedToppingId?: string | null;
  /** 드래그 중 케이크 영역 강조 */
  dragOverCake?: boolean;
  onDragOverCake?: (over: boolean) => void;
  onDropOnCake?: (toppingId: string, viewX: number, viewY: number) => void;
  /** armed 상태에서 케이크에서 포인터를 뗄 때 viewBox 좌표로 배치(탭·클릭 모두) */
  onCakeClickWithArm?: (viewX: number, viewY: number) => void;
  /** 우클릭 시 viewBox 좌표(가장 가까운 장식 제거 등) */
  onCakeContextMenu?: (viewX: number, viewY: number) => void;
  /** 선택된 인덱스 장식 삭제(Delete/Backspace) */
  onRemoveToppingAt?: (index: number) => void;
  /** 올린 장식 이동·회전 */
  onUpdateTopping?: (index: number, patch: Partial<Pick<PlacedTopping, 'x' | 'y' | 'rotationDeg'>>) => void;
  /** 테두리·카드 없이 SVG만(시트 고르기 캐러셀 등) */
  bare?: boolean;
}

/** viewBox 원점에 그림 — 바깥 `<g transform="translate(x,y) rotate">`로 위치 */
function ToppingGlyphAtOrigin({ id }: { id: string }) {
  const opt = CAKE_TOPPINGS.find((t) => t.id === id);
  const imgUrl = opt?.toppingImageUrl?.trim() ?? '';
  if (!imgUrl) return null;
  const s = opt.toppingVisualSize ?? 40;
  return (
    <g>
      <image
        href={imgUrl}
        x={-s / 2}
        y={-s}
        width={s}
        height={s}
        preserveAspectRatio="xMidYMax meet"
      />
    </g>
  );
}

type DragState =
  | { kind: 'idle' }
  | {
      kind: 'pending-move';
      index: number;
      pid: number;
      sx: number;
      sy: number;
      ox: number;
      oy: number;
    }
  | {
      kind: 'move';
      index: number;
      pid: number;
      ox: number;
      oy: number;
      grabX: number;
      grabY: number;
    }
  | { kind: 'rotate'; index: number; pid: number; cx: number; cy: number; angleOffset: number };

const MOVE_THRESH = 5;

function DecoratedCakePreview({
  design,
  size = 200,
  interactive,
  onClick,
  ariaLabel,
  slotEditEnabled,
  armedToppingId,
  dragOverCake,
  onDragOverCake,
  onDropOnCake,
  onCakeClickWithArm,
  onCakeContextMenu,
  onRemoveToppingAt,
  onUpdateTopping,
  bare,
}: DecoratedCakePreviewProps) {
  const sheet = getSheetById(design.sheetId);
  const [c1, c2, c3] = sheet.layerColors;
  const toppings = design.toppings ?? [];
  const clipId = useId().replace(/:/g, '');
  const sheetImageUrl = sheet.sheetImageUrl?.trim() ?? '';
  const useImageSheet = sheetImageUrl.length > 0;
  const nameLight =
    sheet.nameOnCakeUsesLightInk === true || (!useImageSheet && sheet.id === 'chocolate');

  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<DragState>({ kind: 'idle' });
  const [selectedToppingIndex, setSelectedToppingIndex] = useState<number | null>(null);

  const canEditToppings = Boolean(slotEditEnabled && onUpdateTopping);

  useEffect(() => {
    setSelectedToppingIndex((si) => (si !== null && si >= toppings.length ? null : si));
  }, [toppings.length]);

  /** 리마운트 등으로 캡처 원에 pointerup이 안 오면에도 드래그 종료 */
  useEffect(() => {
    if (!canEditToppings) return;
    const clearDragForPointer = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (d.kind === 'idle') return;
      if (d.pid !== ev.pointerId) return;
      dragRef.current = { kind: 'idle' };
    };
    window.addEventListener('pointerup', clearDragForPointer);
    window.addEventListener('pointercancel', clearDragForPointer);
    return () => {
      window.removeEventListener('pointerup', clearDragForPointer);
      window.removeEventListener('pointercancel', clearDragForPointer);
    };
  }, [canEditToppings]);

  useEffect(() => {
    if (!slotEditEnabled || selectedToppingIndex === null || !onRemoveToppingAt) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      e.preventDefault();
      onRemoveToppingAt(selectedToppingIndex);
      setSelectedToppingIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slotEditEnabled, selectedToppingIndex, onRemoveToppingAt]);

  const finishDragIfAny = useCallback((el: Element, pointerId: number) => {
    try {
      el.releasePointerCapture(pointerId);
    } catch {
      /* 이미 해제됨 */
    }
    dragRef.current = { kind: 'idle' };
  }, []);

  /** viewBox="-100 -90 200 220" → 세로 끝 y = -90 + 220 */
  const VIEWBOX_Y_MAX = -90 + 220;
  const imgSheetH = 148;
  const imgSheetTop =
    bare && useImageSheet ? VIEWBOX_Y_MAX - 8 - imgSheetH : -72;
  const clipPadX = 8;
  const clipRx = 28;
  const vectorVisualBottom = 88;

  const cakeLayers = useImageSheet ? (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect
            x={-100 + clipPadX}
            y={imgSheetTop}
            width={200 - clipPadX * 2}
            height={imgSheetH}
            rx={clipRx}
            ry={clipRx}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <image
          href={sheetImageUrl}
          x={-100}
          y={imgSheetTop}
          width={200}
          height={imgSheetH}
          preserveAspectRatio="xMidYMid slice"
        />
      </g>
    </>
  ) : (
    <g transform={`translate(0, ${bare ? VIEWBOX_Y_MAX - 8 - vectorVisualBottom : 0})`}>
      {bare ? <ellipse cx={0} cy={78} rx={62} ry={10} fill="rgba(0,0,0,0.12)" /> : null}
      <ellipse cx={0} cy={58} rx={70} ry={22} fill={c1} stroke={sheet.strokeColor} strokeWidth={1.2} />
      <ellipse cx={0} cy={52} rx={70} ry={22} fill={c1} stroke={sheet.strokeColor} strokeWidth={1} />
      <ellipse cx={0} cy={28} rx={54} ry={18} fill={c2} stroke={sheet.strokeColor} strokeWidth={1.1} />
      <ellipse cx={0} cy={23} rx={54} ry={18} fill={c2} stroke={sheet.strokeColor} strokeWidth={1} />
      <ellipse cx={0} cy={2} rx={38} ry={14} fill={c3} stroke={sheet.strokeColor} strokeWidth={1} />
      <ellipse cx={0} cy={-2} rx={38} ry={14} fill={c3} stroke={sheet.strokeColor} strokeWidth={1} />
    </g>
  );

  const svg = (
    <svg
      ref={svgRef}
      width={size}
      height={(size * 220) / 200}
      viewBox="-100 -90 200 220"
      style={{
        display: 'block',
        userSelect: 'none',
        touchAction: slotEditEnabled ? 'none' : undefined,
      }}
      aria-hidden={interactive ? true : undefined}
    >
      {!useImageSheet && !bare ? <ellipse cx={0} cy={78} rx={62} ry={10} fill="rgba(0,0,0,0.12)" /> : null}
      {cakeLayers}
      {design.nameOnCake ? (
        <text
          x={0}
          y={24}
          textAnchor="middle"
          fill={nameLight ? '#FFFDE7' : '#4E342E'}
          fontSize={11}
          fontWeight={700}
          fontFamily="var(--font-family-primary, Pretendard), sans-serif"
          style={{ textShadow: nameLight ? '0 1px 2px rgba(0,0,0,0.5)' : '0 0 2px #fff' }}
        >
          {design.nameOnCake.length > 12 ? `${design.nameOnCake.slice(0, 11)}…` : design.nameOnCake}
        </text>
      ) : null}
      {slotEditEnabled ? (
        <rect
          x={-100}
          y={-90}
          width={200}
          height={310}
          fill="transparent"
          stroke={dragOverCake ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'none'}
          strokeWidth={2}
          strokeDasharray={dragOverCake ? '8 6' : undefined}
          style={{
            pointerEvents: 'auto',
            cursor: armedToppingId ? 'copy' : 'default',
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            onDragOverCake?.(true);
          }}
          onDrop={(e) => {
            e.preventDefault();
            onDragOverCake?.(false);
            const svg = svgRef.current;
            if (!svg) return;
            const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
            const id =
              e.dataTransfer.getData('application/x-topping-id') || e.dataTransfer.getData('text/plain');
            const tid = id?.trim();
            if (tid) onDropOnCake?.(tid, p.x, p.y);
          }}
          onPointerUp={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            const svg = svgRef.current;
            if (!svg) return;
            if (armedToppingId && onCakeClickWithArm) {
              const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
              onCakeClickWithArm(p.x, p.y);
              return;
            }
            if (!armedToppingId) setSelectedToppingIndex(null);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            const svg = svgRef.current;
            if (!svg || !onCakeContextMenu) return;
            const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
            onCakeContextMenu(p.x, p.y);
          }}
        />
      ) : null}
      {toppings.map((t, i) => {
        const rot = t.rotationDeg ?? 0;
        const selected = selectedToppingIndex === i;
        return (
          <g key={`${i}-${t.id}`} transform={`translate(${t.x},${t.y})`}>
            {canEditToppings && selected ? (
              <circle
                r={32}
                fill="none"
                stroke="var(--Color-fill-brand-color, #FF6F0F)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                pointerEvents="none"
              />
            ) : null}
            <g transform={`rotate(${rot})`}>
              <ToppingGlyphAtOrigin id={t.id} />
              {canEditToppings ? (
                <circle
                  cx={0}
                  cy={0}
                  r={28}
                  fill="rgba(0,0,0,0.02)"
                  style={{
                    cursor: 'grab',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => {
                    if (!canEditToppings || !onUpdateTopping) return;
                    if (e.pointerType === 'mouse' && e.button !== 0) return;
                    e.stopPropagation();
                    setSelectedToppingIndex(i);
                    const svg = svgRef.current;
                    if (!svg) return;
                    const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                    dragRef.current = {
                      kind: 'pending-move',
                      index: i,
                      pid: e.pointerId,
                      sx: p.x,
                      sy: p.y,
                      ox: t.x,
                      oy: t.y,
                    };
                    (e.currentTarget as SVGCircleElement).setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    if (!canEditToppings || !onUpdateTopping) return;
                    const svg = svgRef.current;
                    if (!svg) return;
                    const d = dragRef.current;
                    const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                    if (d.kind === 'pending-move' && d.index === i && d.pid === e.pointerId) {
                      if (Math.hypot(p.x - d.sx, p.y - d.sy) > MOVE_THRESH) {
                        dragRef.current = {
                          kind: 'move',
                          index: i,
                          pid: e.pointerId,
                          ox: d.ox,
                          oy: d.oy,
                          grabX: p.x,
                          grabY: p.y,
                        };
                      }
                      return;
                    }
                    if (d.kind === 'move' && d.index === i && d.pid === e.pointerId) {
                      onUpdateTopping(i, {
                        x: d.ox + (p.x - d.grabX),
                        y: d.oy + (p.y - d.grabY),
                      });
                    }
                  }}
                  onPointerUp={(e) => {
                    if (!canEditToppings) return;
                    const d0 = dragRef.current;
                    const blockArmedPlace = d0.kind === 'move' || d0.kind === 'rotate';
                    if (d0.kind !== 'idle' && d0.pid === e.pointerId) {
                      finishDragIfAny(e.currentTarget, e.pointerId);
                    }
                    if (!blockArmedPlace && armedToppingId && onCakeClickWithArm && svgRef.current) {
                      const p = clientPointToSvgViewBox(svgRef.current, e.clientX, e.clientY);
                      onCakeClickWithArm(p.x, p.y);
                      e.stopPropagation();
                    }
                  }}
                  onPointerCancel={(e) => {
                    if (dragRef.current.kind !== 'idle' && dragRef.current.pid === e.pointerId) {
                      finishDragIfAny(e.currentTarget, e.pointerId);
                    }
                  }}
                  onLostPointerCapture={(e) => {
                    const d = dragRef.current;
                    if (d.kind !== 'idle' && d.pid === e.pointerId) {
                      dragRef.current = { kind: 'idle' };
                    }
                  }}
                  onContextMenu={
                    slotEditEnabled && onCakeContextMenu
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const svg = svgRef.current;
                          if (!svg) return;
                          const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                          onCakeContextMenu(p.x, p.y);
                        }
                      : undefined
                  }
                />
              ) : null}
            </g>
            {canEditToppings && selected ? (
              <circle
                cx={36}
                cy={0}
                r={9}
                fill="var(--Color-fill-brand-color, #FF6F0F)"
                stroke="var(--Color-background-normal, #fff)"
                strokeWidth={2}
                style={{ cursor: 'alias', touchAction: 'none' }}
                onPointerDown={(e) => {
                  if (!canEditToppings || !onUpdateTopping) return;
                  if (e.pointerType === 'mouse' && e.button !== 0) return;
                  e.stopPropagation();
                  setSelectedToppingIndex(i);
                  const svg = svgRef.current;
                  if (!svg) return;
                  const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                  const pa = pointerAngleDeg(t.x, t.y, p.x, p.y);
                  const angleOffset = rot - pa;
                  dragRef.current = {
                    kind: 'rotate',
                    index: i,
                    pid: e.pointerId,
                    cx: t.x,
                    cy: t.y,
                    angleOffset,
                  };
                  (e.currentTarget as SVGCircleElement).setPointerCapture(e.pointerId);
                }}
                onPointerMove={(e) => {
                  if (!canEditToppings || !onUpdateTopping) return;
                  const svg = svgRef.current;
                  if (!svg) return;
                  const d = dragRef.current;
                  if (d.kind !== 'rotate' || d.index !== i || d.pid !== e.pointerId) return;
                  const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                  const pa = pointerAngleDeg(d.cx, d.cy, p.x, p.y);
                  onUpdateTopping(i, { rotationDeg: normalizeRotationDeg(pa + d.angleOffset) });
                }}
                onPointerUp={(e) => {
                  if (dragRef.current.kind === 'rotate' && dragRef.current.pid === e.pointerId) {
                    finishDragIfAny(e.currentTarget, e.pointerId);
                  }
                  e.stopPropagation();
                }}
                onPointerCancel={(e) => {
                  if (dragRef.current.kind !== 'idle' && dragRef.current.pid === e.pointerId) {
                    finishDragIfAny(e.currentTarget, e.pointerId);
                  }
                }}
                onLostPointerCapture={(e) => {
                  const d = dragRef.current;
                  if (d.kind !== 'idle' && d.pid === e.pointerId) {
                    dragRef.current = { kind: 'idle' };
                  }
                }}
                onContextMenu={
                  slotEditEnabled && onCakeContextMenu
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const svg = svgRef.current;
                        if (!svg) return;
                        const p = clientPointToSvgViewBox(svg, e.clientX, e.clientY);
                        onCakeContextMenu(p.x, p.y);
                      }
                    : undefined
                }
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );

  if (interactive && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          padding: bare ? 0 : 8,
          border: bare ? 'none' : '1px solid var(--Color-line-normal, #F0F0F0)',
          background: bare ? 'transparent' : 'var(--Color-background-normal, #fff)',
          cursor: 'pointer',
          borderRadius: bare ? 0 : 14,
          lineHeight: 0,
          boxShadow: bare ? 'none' : '0 4px 14px rgba(41,48,68,0.1)',
        }}
        aria-label={ariaLabel}
      >
        {svg}
      </button>
    );
  }

  if (bare) {
    return svg;
  }

  return (
    <div
      style={{
        padding: 8,
        lineHeight: 0,
        borderRadius: 14,
        border: '1px solid var(--Color-line-normal, #F0F0F0)',
        background: 'var(--Color-background-normal, #fff)',
        display: 'inline-block',
      }}
    >
      {svg}
    </div>
  );
}

export default DecoratedCakePreview;
